import React, { useCallback, useEffect, useRef, useState } from 'react';
import './SwipeToTrade.css';

const KNOB_SIZE = 40;
const TRACK_PAD = 4;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

const SwipeToTrade = ({
  variant = 'buy',
  label = 'Buy',
  hint = '',
  disabled = false,
  direction = 'ltr',
  confirmRatio = 0.82,
  onConfirm,
  onFail,
}) => {
  const trackRef = useRef(null);
  const knobRef = useRef(null);
  const draggingRef = useRef(false);
  const startXRef = useRef(0);
  const startProgressRef = useRef(0);
  const maxOffsetRef = useRef(0);
  const progressRef = useRef(0);
  const confirmedRef = useRef(false);
  const disabledRef = useRef(disabled);
  const samplesRef = useRef([]);
  const startTimeRef = useRef(0);
  const reachedRef = useRef(0);
  const pointerIdRef = useRef(null);
  const listenersRef = useRef(null);
  const confirmRatioRef = useRef(confirmRatio);
  const onConfirmRef = useRef(onConfirm);
  const onFailRef = useRef(onFail);
  const isRtlRef = useRef(direction === 'rtl');

  const [size, setSize] = useState({ width: 0, height: 0 });
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const isRtl = direction === 'rtl';
  confirmRatioRef.current = confirmRatio;
  onConfirmRef.current = onConfirm;
  onFailRef.current = onFail;
  isRtlRef.current = isRtl;

  const detachWindowListeners = () => {
    const listeners = listenersRef.current;
    if (!listeners) return;
    window.removeEventListener('pointermove', listeners.move, true);
    window.removeEventListener('pointerup', listeners.up, true);
    window.removeEventListener('pointercancel', listeners.up, true);
    window.removeEventListener('touchmove', listeners.touchMove, true);
    window.removeEventListener('touchend', listeners.touchEnd);
    window.removeEventListener('touchcancel', listeners.touchEnd);
    listenersRef.current = null;
  };
  const detachRef = useRef(detachWindowListeners);
  detachRef.current = detachWindowListeners;

  const updateProgress = (value) => {
    const next = clamp(value, 0, 1);
    progressRef.current = next;
    reachedRef.current = Math.max(reachedRef.current, next);
    setProgress(next);
  };

  const reset = useCallback(() => {
    detachRef.current();
    confirmedRef.current = false;
    draggingRef.current = false;
    pointerIdRef.current = null;
    reachedRef.current = 0;
    samplesRef.current = [];
    setConfirmed(false);
    setDragging(false);
    updateProgress(0);
  }, []);

  useEffect(() => {
    disabledRef.current = disabled;
  }, [disabled]);

  useEffect(() => () => detachRef.current(), []);

  useEffect(() => {
    if (!disabled) {
      reset();
    }
  }, [disabled, reset]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return undefined;

    const measure = () => {
      setSize({
        width: track.clientWidth,
        height: track.clientHeight,
      });
    };

    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(measure);
    observer.observe(track);
    return () => observer.disconnect();
  }, [direction]);

  const getMaxOffset = () => {
    const track = trackRef.current;
    const knob = knobRef.current;
    if (!track || !knob) return Math.max(0, size.width - KNOB_SIZE - TRACK_PAD * 2);
    return Math.max(0, track.clientWidth - knob.offsetWidth - TRACK_PAD * 2);
  };

  const isEditingField = (node) => (
    !!node
    && node !== document.body
    && (node.tagName === 'INPUT' || node.tagName === 'TEXTAREA' || node.isContentEditable)
  );

  const passesHumanChecks = () => {
    // A completed drag is enough. Phones often deliver only a couple of
    // coalesced touch moves, and a real flick can finish in well under 220ms.
    if (samplesRef.current.length < 2) return false;
    return progressRef.current >= confirmRatioRef.current;
  };

  const completeSwipe = () => {
    confirmedRef.current = true;
    draggingRef.current = false;
    pointerIdRef.current = null;
    setConfirmed(true);
    setDragging(false);
    updateProgress(1);
    if (typeof onConfirmRef.current === 'function') {
      onConfirmRef.current();
    }
    window.setTimeout(() => {
      if (!disabledRef.current) {
        reset();
      }
    }, 450);
  };

  const failAndReset = (allowReroll) => {
    const reached = reachedRef.current;
    updateProgress(0);
    if (allowReroll && reached >= 0.25 && typeof onFailRef.current === 'function') {
      onFailRef.current();
      return;
    }
    reachedRef.current = 0;
  };

  const finishDrag = (event) => {
    if (!draggingRef.current) return;
    if (event && pointerIdRef.current != null && event.pointerId !== pointerIdRef.current) return;
    detachWindowListeners();
    draggingRef.current = false;
    pointerIdRef.current = null;
    setDragging(false);

    if (passesHumanChecks()) {
      completeSwipe();
      return;
    }
    // A scroll/keyboard cancellation should not flip the swipe direction.
    failAndReset(!event || event.type !== 'pointercancel');
  };

  const handlePointerDown = (event) => {
    if (disabled || confirmedRef.current || !event.isTrusted) return;
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    event.stopPropagation();
    event.preventDefault();

    // Limit orders leave the price field focused. Blurring it mid-swipe resizes
    // the screen and the browser cancels the touch, so the knob springs back.
    const active = document.activeElement;
    if (isEditingField(active)) {
      active.blur();
      // A mouse drag can continue after blur. A touch is cancelled when the
      // keyboard closes, so this tap only dismisses the keyboard.
      if (event.pointerType !== 'mouse') return;
    }

    draggingRef.current = true;
    pointerIdRef.current = event.pointerId;
    setDragging(true);
    startXRef.current = event.clientX;
    startProgressRef.current = progressRef.current;
    maxOffsetRef.current = getMaxOffset();
    startTimeRef.current = Date.now();
    samplesRef.current = [{ x: event.clientX, y: event.clientY }];

    const move = (moveEvent) => {
      if (!draggingRef.current || !moveEvent.isTrusted) return;
      if (moveEvent.pointerId !== pointerIdRef.current) return;
      if (moveEvent.cancelable) moveEvent.preventDefault();
      applyPoint(moveEvent.clientX, moveEvent.clientY);
    };

    const up = (upEvent) => {
      finishDrag(upEvent);
    };

    const applyPoint = (clientX, clientY) => {
      samplesRef.current.push({ x: clientX, y: clientY });
      const max = maxOffsetRef.current || getMaxOffset();
      if (max <= 0) return;
      const delta = isRtlRef.current ? startXRef.current - clientX : clientX - startXRef.current;
      updateProgress((startProgressRef.current * max + delta) / max);
    };

    const touchMove = (touchEvent) => {
      if (!draggingRef.current) return;
      if (touchEvent.cancelable) touchEvent.preventDefault();
      const touch = touchEvent.touches && touchEvent.touches[0];
      if (!touch) return;
      applyPoint(touch.clientX, touch.clientY);
    };

    const touchEnd = (touchEvent) => {
      if (!draggingRef.current) return;
      const touch = touchEvent.changedTouches && touchEvent.changedTouches[0];
      if (touch) applyPoint(touch.clientX, touch.clientY);
      finishDrag({
        pointerId: pointerIdRef.current,
        type: touchEvent.type === 'touchcancel' ? 'pointercancel' : 'touchend',
      });
    };

    listenersRef.current = { move, up, touchMove, touchEnd };
    window.addEventListener('pointermove', move, true);
    window.addEventListener('pointerup', up, true);
    window.addEventListener('pointercancel', up, true);
    window.addEventListener('touchmove', touchMove, { passive: false, capture: true });
    window.addEventListener('touchend', touchEnd);
    window.addEventListener('touchcancel', touchEnd);

    if (event.currentTarget.setPointerCapture) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (err) {
        // Capture is unreliable inside a scrolling bottom sheet. Window
        // listeners still follow the finger after the knob moves out from under it.
      }
    }
  };

  const maxOffset = maxOffsetRef.current || Math.max(0, size.width - KNOB_SIZE - TRACK_PAD * 2);
  const fillWidth = progress * maxOffset + KNOB_SIZE + TRACK_PAD + 4;
  const displayLabel = hint ? `${label} ${hint}` : label;
  const statusText = disabled && confirmed
    ? `Placing ${label}...`
    : `Swipe ${isRtl ? 'left' : 'right'} to ${displayLabel}`;

  return (
    <div
      ref={trackRef}
      className={[
        'swipe-to-trade',
        `swipe-to-trade--${variant}`,
        isRtl ? 'swipe-to-trade--rtl' : 'swipe-to-trade--ltr',
        dragging ? 'is-dragging' : '',
        confirmed ? 'is-confirmed' : '',
        disabled ? 'is-disabled' : '',
        progress > 0.12 ? 'is-filling' : '',
      ].filter(Boolean).join(' ')}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress * 100)}
      aria-label={statusText}
      aria-disabled={disabled}
      onContextMenu={(event) => event.preventDefault()}
      onPointerDown={(event) => {
        if (knobRef.current && knobRef.current.contains(event.target)) return;
        const active = document.activeElement;
        if (isEditingField(active)) active.blur();
      }}
    >
      <div
        className="swipe-to-trade__fill"
        style={{ width: `${fillWidth}px` }}
      />
      <span className="swipe-to-trade__label">{statusText}</span>
      <div
        ref={knobRef}
        className="swipe-to-trade__knob"
        style={{
          left: isRtl ? 'auto' : `${TRACK_PAD}px`,
          right: isRtl ? `${TRACK_PAD}px` : 'auto',
          transform: `translateX(${(isRtl ? -progress : progress) * maxOffset}px)`,
        }}
        onPointerDown={handlePointerDown}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
        }}
      >
        <svg viewBox="0 0 24 24" className="swipe-to-trade__arrow" aria-hidden="true">
          <path d="M9.3 5.7a1 1 0 0 1 1.4 0l6 6a1 1 0 0 1 0 1.4l-6 6a1 1 0 1 1-1.4-1.4L14.58 13H5a1 1 0 1 1 0-2h9.58L9.3 7.1a1 1 0 0 1 0-1.4z" />
        </svg>
      </div>
    </div>
  );
};

export default SwipeToTrade;
